import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, Text } from '@vibe/core';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PickedBoard {
  id: string;
  name: string;
}

interface BoardPickerProps {
  /** Currently selected board (null if nothing selected) */
  value: PickedBoard | null;
  /** Called when the user picks a board */
  onChange: (board: PickedBoard | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Board IDs to exclude from the dropdown results */
  excludeIds?: Set<number>;
}

// ─── GraphQL ──────────────────────────────────────────────────────────────────

/**
 * Convention: Every dropdown that returns data from monday.com should work like
 * a search. Load first 10 entities by default (sorted by recently used), then
 * when the user types 2+ characters, search/filter for matching results.
 *
 * monday.com boards API doesn't support server-side text search, so we load a
 * batch and filter client-side.
 */
const BOARDS_QUERY = `
  query GetBoards($limit: Int!, $page: Int!) {
    boards(limit: $limit, page: $page, order_by: used_at) {
      id
      name
      type
    }
  }
`;

const DEFAULT_LIMIT = 10;
const SEARCH_LIMIT = 100;
const SEARCH_MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

// ─── Component ────────────────────────────────────────────────────────────────

const BoardPicker: React.FC<BoardPickerProps> = ({
  value,
  onChange,
  placeholder = 'Search for a board…',
  disabled = false,
  excludeIds,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<PickedBoard[]>([]);
  const [defaultBoards, setDefaultBoards] = useState<PickedBoard[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  // Load default boards (first 10, sorted by recently used) on mount
  useEffect(() => {
    let cancelled = false;

    async function loadDefaults() {
      setIsLoadingDefaults(true);
      try {
        const response = await monday.api(BOARDS_QUERY, {
          variables: { limit: DEFAULT_LIMIT, page: 1 },
        });
        const data = response.data as {
          boards?: Array<{ id: string | number; name: string; type?: string }>;
        };
        if (!cancelled) {
          setDefaultBoards(
            (data.boards ?? [])
              .filter((b) => b.type !== 'sub_items_board')
              .map((b) => ({
                id: String(b.id),
                name: b.name,
              })),
          );
        }
      } catch {
        // Silently fail – the user can still type to search
      } finally {
        if (!cancelled) setIsLoadingDefaults(false);
      }
    }

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, []);

  // Search boards when debounced query changes (2+ characters)
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < SEARCH_MIN_CHARS) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search() {
      setIsSearching(true);
      setSearchError(null);
      try {
        // Fetch a larger batch so we can filter client-side
        const response = await monday.api(BOARDS_QUERY, {
          variables: { limit: SEARCH_LIMIT, page: 1 },
        });
        const data = response.data as {
          boards?: Array<{ id: string | number; name: string; type?: string }>;
        };

        if (cancelled) return;

        const lowerQ = debouncedQuery.toLowerCase();
        const matched: PickedBoard[] = [];

        for (const b of data.boards ?? []) {
          if (b.type === 'sub_items_board') continue;
          if (b.name.toLowerCase().includes(lowerQ)) {
            matched.push({ id: String(b.id), name: b.name });
          }
          if (matched.length >= 20) break;
        }

        setResults(matched);
      } catch (err) {
        if (!cancelled) {
          setSearchError(
            err instanceof Error ? err.message : 'Search failed',
          );
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }

    search();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      if (!isOpen) setIsOpen(true);
    },
    [isOpen],
  );

  const handleSelect = useCallback(
    (board: PickedBoard) => {
      onChange(board);
      setQuery('');
      setIsOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery('');
    inputRef.current?.focus();
  }, [onChange]);

  // Determine which items to show in the dropdown
  const isSearchMode = debouncedQuery.length >= SEARCH_MIN_CHARS;
  const sourceItems = isSearchMode ? results : defaultBoards;
  const displayItems = excludeIds && excludeIds.size > 0
    ? sourceItems.filter((b) => !excludeIds.has(Number(b.id)))
    : sourceItems;

  return (
    <div className="rp-board-picker" ref={containerRef}>
      {/* Selected board display */}
      {value && !isOpen ? (
        <div className="rp-board-picker__selected">
          <div className="rp-board-picker__selected-info">
            <span className="rp-board-picker__selected-name">{value.name}</span>
            <span className="rp-board-picker__selected-id">ID: {value.id}</span>
          </div>
          {!disabled && (
            <button
              className="rp-board-picker__clear"
              onClick={handleClear}
              type="button"
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <input
          ref={inputRef}
          className="rp-board-picker__input"
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={value ? value.name : placeholder}
          disabled={disabled}
          autoComplete="off"
        />
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="rp-board-picker__dropdown">
          {/* Searching indicator */}
          {(isSearching || isLoadingDefaults) && (
            <div className="rp-board-picker__dropdown-status">
              <Loader size={Loader.sizes.XS} />
              <span>{isSearchMode ? 'Searching…' : 'Loading boards…'}</span>
            </div>
          )}

          {/* Error */}
          {searchError && (
            <div className="rp-board-picker__dropdown-status rp-board-picker__dropdown-status--error">
              <Text type={Text.types.TEXT2} style={{ color: '#e2445c' }}>
                {searchError}
              </Text>
            </div>
          )}

          {/* Hint to type more characters */}
          {!isSearching &&
            !isLoadingDefaults &&
            !isSearchMode &&
            defaultBoards.length > 0 && (
              <div className="rp-board-picker__dropdown-hint">
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  Recently used boards · type 2+ characters to search
                </Text>
              </div>
            )}

          {/* No results */}
          {!isSearching &&
            !isLoadingDefaults &&
            !searchError &&
            isSearchMode &&
            results.length === 0 && (
              <div className="rp-board-picker__dropdown-status">
                <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                  No boards found
                </Text>
              </div>
            )}

          {/* Board items */}
          {displayItems.map((board) => (
            <button
              key={board.id}
              className="rp-board-picker__option"
              onClick={() => handleSelect(board)}
              type="button"
            >
              <span className="rp-board-picker__option-name">{board.name}</span>
              <span className="rp-board-picker__option-id">ID: {board.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BoardPicker;
